import { useCallback, useEffect, useState } from "react";
import { getDistribution } from "../../../services/clearing/station-performance";
import type { DistributionReport, ReportingOptions } from "../../../types/stationPerformance";

interface UseFleetDistributionArgs {
  month: string;
  measure: string;
  options: ReportingOptions;
  /** Earliest month to include in the spread trend. Omit for a single-month view. */
  trendFrom?: string | null;
}

/** The across-fleet distribution for one month, optionally with the spread trend behind it. */
export const useFleetDistribution = ({
  month,
  measure,
  options,
  trendFrom = null,
}: UseFleetDistributionArgs) => {
  const [report, setReport] = useState<DistributionReport | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [neverComputed, setNeverComputed] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDistribution = useCallback(async () => {
    setNeverComputed(null);
    setError(null);
    // Variance is never channel-scoped, so the provider filter is dropped rather than sent and refused.
    const result = await getDistribution({
      scope: "ACROSS_UNITS",
      measure,
      month,
      from: trendFrom,
      to: trendFrom ? month : null,
      options: { ...options, provider: null },
    });
    if (result?.success) {
      setReport(result.data ?? null);
    } else if (result?.neverComputed) {
      setNeverComputed(result.months ?? [month]);
      setReport(null);
    } else {
      setError(result?.message ?? "Could not load the fleet distribution.");
    }
    setInitialLoading(false);
  }, [month, measure, options, trendFrom]);

  useEffect(() => {
    fetchDistribution();
  }, [fetchDistribution]);

  return { report, initialLoading, neverComputed, error, fetchDistribution };
};
